import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AsamcoAuthService } from '@asamco/base';
import { User, UserService } from '@asamco/user-management/shared';
import { pathJoin, skipAuthInterceptor } from '@asamco/utils';
import { BehaviorSubject, combineLatest, from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuditLogData } from '../../interfaces/audit-log-data.interface';
import { Company } from '../../interfaces/company.interface';
import { LinkTemplate } from '../../interfaces/link-template.interface';
import { LinkType } from '../../interfaces/link-type.interface';
import { SelectedCompanyService } from '../../selected-company/selected-company.service';
import { LinkTemplateService } from '../../services/link-template.service';
import { LinkTypeService } from '../../services/link-type.service';
import { AsyncPipe, DatePipe } from '@angular/common';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxoSelectionModule, DxoFilterRowModule, DxiColumnModule } from 'devextreme-angular/ui/nested';
import { DxDataGridModule } from 'devextreme-angular';
import ODataStore from 'devextreme/data/odata/store';
@Component({
    selector: 'app-shell-admin-audit-logs-links',
    templateUrl: './admin-audit-logs-links.component.html',
    styleUrls: ['./admin-audit-logs-links.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        DxDataGridModule,
        DxoSelectionModule,
        DxoFilterRowModule,
        DxiColumnModule,
        DxTemplateModule,
        AsyncPipe,
        DatePipe,
    ],
})
export class AdminAuditLogsLinksComponent implements OnInit {
	auditLogData: ODataStore<AuditLogData, any>;

	private userBehaviorSubject: BehaviorSubject<User[]> = new BehaviorSubject<User[]>(null);
	private companyBehaviorSubject: BehaviorSubject<Company[]> = new BehaviorSubject<Company[]>(null);
	private linkTemplateBehaviorSubject: BehaviorSubject<LinkTemplate[]> = new BehaviorSubject<LinkTemplate[]>(null);
	private linkTypeBehaviorSubject: BehaviorSubject<LinkType[]> = new BehaviorSubject<LinkType[]>(null);

	constructor(
		private userService: UserService,
		private companyService: SelectedCompanyService,
		private auth: AsamcoAuthService,
		private linkTemplateService: LinkTemplateService,
		private linkTypeService: LinkTypeService,
		private http: HttpClient,
		private asamcoAuthService: AsamcoAuthService,
	) {}

	ngOnInit(): void {
		this.retrieveRequiredData();

		this.asamcoAuthService.authTokenChanged$.subscribe(
					(token) => {
						if (token.token) {
							this.auditLogData = new ODataStore<AuditLogData>({
								deserializeDates: false,
								url: this.asamcoAuthService.userSession.backend.odataBaseUrl + 'AuditLogData',
								version: 4,
								beforeSend: (e) => {
									e.headers = {
										"Authorization": `Bearer ${token.token}`,
										"X-Audit-Log-Combination": 'Link|DELETE,Link|CREATE',
									};
								}
							});
						}
					},
					(error) => {
						console.error('Error fetching the token:', error);
					}
				);
	}

	retrieveRequiredData() {
		combineLatest([
			this.userService.getAll(),
			this.companyService.getCompanies(),
			this.linkTemplateService.getAllLinkTemplates(),
			this.linkTypeService.getAllLinkTemplates(),
		]).subscribe(([users, companies, linkTemplates, linkTypes]) => {
			this.userBehaviorSubject.next(users);
			this.companyBehaviorSubject.next(companies);
			this.linkTemplateBehaviorSubject.next(linkTemplates);
			this.linkTypeBehaviorSubject.next(linkTypes);
		});
	}

	getCompanyAuditData(): Observable<AuditLogData[]> {
		let ownParams = new HttpParams();

		ownParams = ownParams.set(
			'$filter',
			`(RecordType eq 'Link' and AuditAction eq 'DELETE') or (RecordType eq 'Link' and AuditAction eq 'CREATE')`
		);

		ownParams = ownParams.set('$select', `timeStamp,userId,userName,auditAction,additionalData, recordType, recordId`);

		return from(this.auth.getPrimaryTokenAsync()).pipe(switchMap(
			pt => this.http.get(pathJoin([this.auth.userSession.backend.odataBaseUrl, 'dynamic/_AL3_vw_AuditLogData']), {
				headers: skipAuthInterceptor(new HttpHeaders().set('Authorization', 'Bearer ' + pt)),
				params: ownParams,
			})
		),
		// Map to odata value to filter out context
		map((data: any) => {
			return data.value;
		}));
	}

	public convertAdditionalDataToReadableText(auditLogData: AuditLogData): string {
		//1: Convert additionalData to corresponding object based on the RecordType
		switch (auditLogData.recordType) {
			case 'Link':
				let data = JSON.parse(auditLogData.additionalData);
				let userName = this.userBehaviorSubject.value?.find(item => item.id == auditLogData.userId)?.displayName || '?';
				let companyName = this.companyBehaviorSubject.value?.find(item => item.id == data?.CompanyId)?.name || '?';
				let fromLinkTemplate = this.linkTemplateBehaviorSubject.value?.find(item => item.id == data?.FromLinkTemplateId);
				let toLinkTemplate = this.linkTemplateBehaviorSubject.value?.find(item => item.id == data?.ToLinkTemplateId);
				let linkType = this.linkTypeBehaviorSubject.value?.find(item => item.id == fromLinkTemplate?.linkTypeId)?.description || '?';

				if (auditLogData.auditAction == 'CREATE') {
					return `Link '${fromLinkTemplate?.description}' '${toLinkTemplate?.description}' of type '${linkType}' has been created for company '${companyName}' by user '${userName}'`;
				} else if (auditLogData.auditAction == 'DELETE') {
					return `Link '${fromLinkTemplate?.description}' '${toLinkTemplate?.description}' of type '${linkType}' has been deleted for company '${companyName}' by user '${userName}'`;
				}

				break;
		}
		return auditLogData.additionalData;
	}
}
